/**
 * DILACODE INC. — corporate facts, single source of truth.
 *
 * Every public corporate surface (metadata, JSON-LD, footer, contact, legal,
 * llms.txt generators, tests) reads from here. Nothing retypes these strings.
 *
 * Rules that apply to anything added to this file:
 *   1. Only facts that can be checked against a document or a live URL.
 *   2. No customers, revenue, headcount, awards, partnerships, funding,
 *      certifications, operating history or impact numbers.
 *   3. The corporation number is intentionally NOT emitted into schema.org
 *      JSON-LD — there is no schema property that carries an Ontario
 *      corporation number without misrepresenting it (`taxID`/`vatID`/
 *      `duns`/`leiCode` all mean something else). It is published as visible
 *      corporate-details content instead, which is what a reviewer verifies.
 */

const publicPhone = '+1-416-834-0963';

export const company = {
  /** Registered legal name, exactly as incorporated. */
  legalName: 'DILACODE INC.',
  /** Display / brand name used in running copy and the wordmark. */
  name: 'DilaCode',
  /** Uppercase wordmark used by the Six Caps display type. */
  wordmark: 'DILACODE',

  /** Ontario corporation number. Visible corporate detail only — see note above. */
  corporationNumber: '1000959255',
  incorporatedIn: 'Ontario, Canada',
  governingLaw: 'Ontario, Canada',

  /**
   * Date the incorporation took effect, per the Ontario corporate record.
   * ISO form drives schema.org `foundingDate`; the label is what humans read.
   */
  incorporationDate: '2024-08-06',
  incorporationDateLabel: 'August 6, 2024',

  address: {
    street: '129 Compton Crescent',
    city: 'Bradford',
    region: 'Ontario',
    regionCode: 'ON',
    postalCode: 'L3Z 2X8',
    country: 'Canada',
    countryCode: 'CA',
  },

  email: 'info@dilacode.com',
  /** E.164-adjacent display form. `telHref` below is the dialable one. */
  phone: publicPhone,
  telHref: publicPhone.replace(/[^\d+]/g, ''),

  contactName: 'Hooshyar Mohammed Rasul',
  contactRole: 'Founder',

  url: 'https://dilacode.com',
  domain: 'dilacode.com',

  /** IANA zone for the registered office — drives the footer's local clock. */
  timeZone: 'America/Toronto',
  timeZoneLabel: 'Toronto',
} as const;

/** "129 Compton Crescent, Bradford, Ontario L3Z 2X8, Canada" */
export const addressLine = `${company.address.street}, ${company.address.city}, ${company.address.region} ${company.address.postalCode}, ${company.address.country}`;

/** Two-line postal form for <address> blocks. */
export const addressLines = [
  company.address.street,
  `${company.address.city}, ${company.address.region} ${company.address.postalCode}, ${company.address.country}`,
] as const;

/** Absolute URL helper so no page hardcodes the host. */
export function absoluteUrl(path = ''): string {
  if (!path || path === '/') return company.url;
  return `${company.url}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Legacy alias. `contactInfo` was exported from `src/lib/data.ts` and is
 * imported in a handful of places; it now resolves to the Canadian entity so
 * no surface can render Erbil contact details by accident.
 */
export const contactInfo = {
  email: company.email,
  phone: company.phone,
  address: addressLine,
  legalName: company.legalName,
};
